import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Selects the already-rendered media for a locally resolved variant.
   *
   * @param {import('./events').VariantSelectedEvent} event
   */
  #handleVariantSelected = (event) => {
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) return;
    if (event.target instanceof HTMLElement && event.target.hasAttribute('data-aknes-m12s-variant-picker')) return;

    const selectedOptions =
      event.target instanceof HTMLElement && Array.isArray(event.target.selectedOptionValues)
        ? event.target.selectedOptionValues
        : [];
    const requestedVariantId = event.detail.variant?.id ? String(event.detail.variant.id) : null;

    this.selectVariantMedia(event.detail.variant, selectedOptions, requestedVariantId);
  };

  selectVariantMedia(variant, selectedOptions = [], requestedVariantId = null) {
    const mediaId = variant?.featured_media?.id;
    const normalizedColor = String(selectedOptions[0] || '').trim().toLowerCase();
    const mediaElements = Array.from(this.querySelectorAll('[data-media-id]'));
    const media = mediaId
      ? mediaElements.find((element) => element.dataset.mediaId === String(mediaId))
      : mediaElements.find((element) => {
          const alt = element.dataset.mediaAlt || element.querySelector('img')?.alt || '';
          return normalizedColor && alt.trim().toLowerCase().includes(normalizedColor);
        });

    if (!media) {
      if (window.Shopify?.designMode && mediaId) {
        console.warn('[AKNES Variant Media] No matching media found for featured_media.id', String(mediaId));
      }
      return false;
    }

    const slide = media.matches('slideshow-slide') ? media : media.closest('slideshow-slide');
    if (!slide) return false;

    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const index = slides.indexOf(slide);
    if (index < 0) return false;
    const targetMediaId = media.dataset.mediaId || String(mediaId || '');

    if (typeof this.slideshow?.select === 'function') {
      this.slideshow.select(index, undefined, { animate: false });
    } else {
      slides.forEach((item) => item.setAttribute('aria-hidden', item === slide ? 'false' : 'true'));
      slide.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });

      this.querySelectorAll('[data-thumbnail-id]').forEach((thumbnail) => {
        const isSelected = thumbnail.dataset.thumbnailId === targetMediaId;
        if (isSelected) thumbnail.setAttribute('aria-selected', 'true');
        else thumbnail.removeAttribute('aria-selected');
      });
    }

    if (window.Shopify?.designMode) {
      console.debug('[AKNES Variant Media]', {
        variantId: variant?.id || null,
        variantTitle: variant?.title || null,
        featuredMedia: variant?.featured_media || null,
        featuredMediaId: variant?.featured_media?.id || null,
      });
      console.debug('[AKNES Variant Media Debug]', {
        selectedOptions,
        requestedVariantId,
        variantId: variant?.id || null,
        variantTitle: variant?.title || null,
        featuredMediaId: variant?.featured_media?.id || null,
        foundMediaElement: true,
        mediaElementId: media.id || null,
        mediaDataset: { ...media.dataset },
      });
    }

    return true;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
